<template>
  <div class="c-imageUploader">
    <div
      class="c-imageUploader_dropzone"
      ref="dropzone"
      @dragover.prevent
      @dragenter.prevent="$emit('update:isHovering', true)"
      @dragleave.prevent="$emit('update:isHovering', false)"
      @drop.prevent.stop="onDropDropzone"
    >
      <slot name="dropzone" />
      <input
        type="file"
        :multiple="max > 1"
        accept="jpg, .jpeg, image/gif, image/jpeg, image/png"
        ref="fileInput"
        @change="onChangeInput"
      />
    </div>
  </div>
</template>
<script>
import ImageCropper from '~/components/molecules/ImageCropper'

// modules
import ImageResizer, { MODES as RESIZE_MODES } from '~/assets/javascripts/image-resizer'
import FileUtil from '~/assets/javascripts/file-util'

// constants
const ALLOW_MIME_TYPES = ['jpeg', 'png', 'gif']

export default {
  name: 'ImageUploader',
  components: {
    ImageCropper
  },
  props: {
    max: {
      type: Number,
      default: 1
    },
    quality: {
      type: Number,
      default: 1,
      validator(v) {
        return v > 0 && v <= 1
      }
    },
    maxWidth: {
      type: Number,
      default: 2000
    },
    maxHeight: {
      type: Number,
      default: null
    },
    maxFileSize: {
      type: Number,
      default: 1024 * 1024 * 3.8 // default: 3.8MB
    },
    resizeMode: {
      type: String,
      default: RESIZE_MODES[0],
      validator(v) {
        return RESIZE_MODES.includes(v)
      }
    },
    isHovering: {
      type: Boolean,
      default: false
    },
    isUploading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      attributes: {
        uri: null,
        changed: false
      }
    }
  },
  methods: {
    openFinder() {
      this.$refs.fileInput.click()
    },
    onDropDropzone(e) {
      this.doUpload(e.dataTransfer.files)
      this.$emit('update:isHovering', false)
    },
    onChangeInput(e) {
      this.doUpload(e.target.files)
    },
    doUpload(fileList) {
      this.$emit('update:isUploading', true)
      let files = this.filterFiles(fileList)
      files.forEach(file => {
        const mimeType = file.type.replace('image/', '')
        const fileName = file.name || ''
        if (ALLOW_MIME_TYPES.includes(mimeType) || fileName.match(/\.heic/i)) {
          this.createFile(file)
            .then(uri => {
              this.$emit('uploaded', uri)
            })
            .catch(e => {
              this.$notify({
                group: 'flash',
                type: 'error',
                title: 'ファイルのアップロードに失敗しました。再度お試しください。'
              })
            })
        } else {
          this.$notify({
            group: 'flash',
            type: 'error',
            title: 'このファイル形式はアップロードできません'
          })
        }
      })
      this.$refs.fileInput.value = null
    },
    filterFiles(fileList) {
      let files = Array.from(fileList)
      if (files.length <= this.max) return files
      files.splice(this.max, files.length - this.max)
      return files
    },
    async createFile(file) {
      let orientation = await FileUtil.getOrientationFromFile(file)
      let image = new Image()
      image.src = FileUtil.fileToUrl(file)

      return new Promise((resolve, reject) => {
        image.onerror = error => reject(error)
        image.onload = async () => {
          try {
            const imageResizer = new ImageResizer(image, orientation, {
              width: this.maxWidth,
              height: this.maxHeight,
              mode: this.resizeMode
            })
            await imageResizer.resize()
            let base64 = imageResizer.canvas.toDataURL('image/jpeg', this.quality)
            if (base64.length > this.maxFileSize) {
              let capacityRatio = this.maxFileSize / base64.length
              base64 = imageResizer.canvas.toDataURL('image/jpeg', capacityRatio)
            }
            resolve(base64)
            this.$emit('update:isUploading', false)
          } catch (e) {
            reject(e)
          }
        }
      })
    }
  }
}
</script>
<style lang="scss" scoped>
.c-imageUploader {
  input[type='file'] {
    display: none;
  }
}
</style>
